import { EventAction } from 'utils/analytics/amplitudeEventConstants';
import {
  GetSeatingAreaOptionsFragment,
  SeatingAreaOptions,
  SeatingAreaOutput,
  useFloorplanRouteQuery,
} from 'generated/graphql';
import { IAsyncRenderContext } from 'contexts/asyncRender';
import { NO_SEATING_AREA_ID } from 'utils/availability';
import { TableIdMap } from 'selectors/routes/calendar/calendarFloorplanSelector';
import { amplitudeSendEvent } from 'utils/analytics/amplitude';
import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import { gql } from '@apollo/client/core';
import { useMemo } from 'react';
import _ from 'lodash';
import pluralize from 'pluralize';

export const NO_SEATING_AREA_TEXT = 'No seating option';

export const encodeSeatingArea: (seatingArea: dashboard.ISeatingArea) => string = (seatingArea) => {
  return seatingArea.indoor
    ? 'INDOOR_' + seatingArea.enumValue
    : 'OUTDOOR_' + seatingArea.enumValue || '';
};

export const decodeSeatingArea: (seatingArea: string) => dashboard.ISeatingArea = (seatingArea) => {
  if (seatingArea.startsWith('INDOOR_')) {
    return {
      enumValue: seatingArea.replace('INDOOR_', ''),
      indoor: true,
    };
  } else if (seatingArea.startsWith('OUTDOOR_')) {
    return {
      enumValue: seatingArea.replace('OUTDOOR_', ''),
      indoor: false,
    };
  } else {
    throw new Error(`Unexpected seating area type: ${seatingArea}`);
  }
};

export const scheduleNeedsSeatingAreaInitialization = (
  schedule: dashboard.BusinessInventorySchedule
) => {
  if (!schedule.usesSeatingAreas) {
    return false;
  }

  const hasActiveTablesMissingSeatingArea =
    schedule.floorplan?.activeTable?.some((table) => !table.seatingArea) ?? false;

  const hasTableCombosMissingSeatingAreas =
    schedule.floorplan?.tableCombination?.some((tableCombination) => {
      return !tableCombination.isArchived && !tableCombination.seatingArea;
    }) ?? false;

  return hasActiveTablesMissingSeatingArea || hasTableCombosMissingSeatingAreas;
};

export const getBestSeatingAreaForTable: (
  seatingAreasInRoom: dashboard.ISeatingArea[],
  table?: dashboard.ITable | null,
  business?: core.Business,
  seatingAreaOptions?: SeatingAreaOptions | null,
  selectedRoomName?: string | null
) => dashboard.ISeatingArea = (
  seatingAreasInRoom,
  table,
  business,
  seatingAreaOptions,
  selectedRoomName
) => {
  // if there are seating areas already in this room,
  // return the most-used one.
  if (seatingAreasInRoom.length > 0) {
    // seatingAreasInRoom should have one entry per table
    // map reduce to see how many tables have each seating area in this room.
    const seatingAreasToTableCount = seatingAreasInRoom
      .map((seatingArea) => encodeSeatingArea(seatingArea))
      .reduce((acc, cur) => {
        acc.set(cur, (acc.get(cur) ?? 0) + 1);
        return acc;
      }, new Map<string, number>());

    // then find the seating area applied to most tables.
    const mostUsedSeatingArea = Array.from(seatingAreasToTableCount.entries()).reduce(
      (acc, cur) => {
        return cur[1] > acc[1] ? cur : acc;
      },
      ['', 0]
    )[0];

    return decodeSeatingArea(mostUsedSeatingArea);
  }

  // next check for exact match to section name
  let exactMatch = findPossibleMatch(seatingAreaOptions, table?.section);
  if (exactMatch) {
    return exactMatch;
  }

  // next check for exact match to table room name
  exactMatch = findPossibleMatch(seatingAreaOptions, table?.room);
  if (exactMatch) {
    return exactMatch;
  }

  // next check for exact match to selected room name
  exactMatch = findPossibleMatch(seatingAreaOptions, selectedRoomName);
  if (exactMatch) {
    return exactMatch;
  }

  // next map a bunch of globally most-used but not directly matching section names
  let matchingMostUsedUnmatched = getMatchingMostUsedUnmatched(business, table?.section);
  if (matchingMostUsedUnmatched) {
    return matchingMostUsedUnmatched;
  }

  // next map a bunch of globally most-used but not directly matching room names
  matchingMostUsedUnmatched = getMatchingMostUsedUnmatched(business, table?.room);
  if (matchingMostUsedUnmatched) {
    return matchingMostUsedUnmatched;
  }

  return getBusinessDefault(business);
};

const findPossibleMatch: (
  seatingAreaOptions?: SeatingAreaOptions | null,
  sectionOrRoom?: string | null
) => dashboard.ISeatingArea | undefined = (seatingAreaOptions, sectionOrRoom) => {
  if (!sectionOrRoom) {
    return undefined;
  }

  // check for indoor matches
  let matchingSeatingAreaOption = seatingAreaOptions?.indoor.find((option) =>
    findExactMatch(option.name, sectionOrRoom)
  );
  if (matchingSeatingAreaOption) {
    return { enumValue: matchingSeatingAreaOption.enumValue, indoor: true };
  }

  // check for outdoor matches
  matchingSeatingAreaOption = seatingAreaOptions?.outdoor.find((option) =>
    findExactMatch(option.name, sectionOrRoom)
  );
  if (matchingSeatingAreaOption) {
    return { enumValue: matchingSeatingAreaOption.enumValue, indoor: false };
  }

  return undefined;
};

const getMatchingMostUsedUnmatched: (
  business?: core.Business,
  sectionOrRoom?: string | null
) => dashboard.ISeatingArea | null = (business, sectionOrRoom) => {
  if (!sectionOrRoom) {
    return null;
  }

  switch (sectionOrRoom.toLowerCase()) {
    case '1. main dining':
    case '1st floor':
    case '2nd floor':
    case 'cafe':
    case 'dining':
    case 'floor plan':
    case 'indoor':
    case 'indoor dining':
    case 'inside':
    case 'main':
    case 'main dining':
    case 'main dining room':
    case 'main floor':
    case 'main floor plan':
    case 'main room':
    case 'mdr':
    case 'restaurant':
      return {
        enumValue: 'DINING_ROOM',
        indoor: true,
      };
    case 'mezzanine':
    case 'tables':
      return {
        enumValue: 'TABLE',
        indoor: true,
      };
    case 'deck':
    case 'front patio':
    case 'back patio':
    case 'lawn':
    case 'outside':
      return {
        enumValue: 'OUTDOOR',
        indoor: false,
      };
    case 'all':
    case 'regular':
      return getBusinessDefault(business);
    default:
      return null;
  }
};

const getBusinessDefault: (business?: core.Business) => dashboard.ISeatingArea = (business) => {
  const tableSeatingArea: dashboard.ISeatingArea = {
    enumValue: 'TABLE',
    indoor: true,
  };

  switch (business?.businessTypeString?.toLowerCase()) {
    case 'winery':
      return {
        enumValue: 'TASTING_ROOM',
        indoor: true,
      };
    case 'bar':
      return {
        enumValue: 'BAR',
        indoor: true,
      };
    default:
      return tableSeatingArea;
  }
};

gql`
  fragment GetSeatingAreaOptions on SeatingAreaOptions {
    indoor {
      enumValue
      name
    }
    outdoor {
      enumValue
      name
    }
  }
`;

export const useGetSeatingAreaNameFromEnumValue = () => {
  const { data } = useFloorplanRouteQuery();

  return useSeatingAreaNameFromEnumValueMapping(data?.seatingAreaOptions);
};

export const useSeatingAreaNameFromEnumValueMapping = (
  data?: GetSeatingAreaOptionsFragment | null
) => {
  return useMemo(() => {
    const options = [...(data?.indoor ?? []), ...(data?.outdoor ?? [])];
    const enumNameOptionNameMapping = new Map<string, string>(
      options.map((option) => [option.enumValue, option.name])
    );

    return (seatingArea: dashboard.ISeatingArea | null | undefined) => {
      if (!seatingArea) {
        return NO_SEATING_AREA_TEXT;
      } else if (seatingArea?.enumValue == null) {
        return '';
      }

      return enumNameOptionNameMapping.get(seatingArea?.enumValue) ?? '';
    };
  }, [data]);
};

export async function validateReservationSeatingArea(
  purchaseSeatingArea: { seatingAreaName?: string; seatingAreaEnumValue?: string },
  selectedTablesToBook: dashboard.Table[],
  asyncRender: IAsyncRenderContext
): Promise<boolean> {
  if (purchaseSeatingArea.seatingAreaEnumValue) {
    const conflictingTables = selectedTablesToBook.filter((value) => {
      const newSeatingAreaEnum = value.seatingArea?.enumValue;
      return newSeatingAreaEnum !== purchaseSeatingArea.seatingAreaEnumValue;
    });
    if (conflictingTables.length > 0) {
      const conflictingTablesName = conflictingTables.map((table) => table.name).join(', ');

      const text = (
        <>
          <p>
            The seating option configured on {pluralize('table', conflictingTables.length)}{' '}
            {conflictingTablesName} {pluralize('does', conflictingTables.length)} not match the
            seating option of "{purchaseSeatingArea.seatingAreaName}" specified in the booking.
          </p>
          <br />
          <p>Are you sure you want to update this table assignment?</p>
        </>
      );
      try {
        await asyncRender.confirm(text, {
          continueText: 'Update table assignment',
          title: 'Seating option does not match',
        });
        amplitudeSendEvent(EventAction.TIMELINE_DRAG_SEATING_AREA_MISMATCH);
      } catch {
        amplitudeSendEvent(EventAction.TIMELINE_DRAG_SEATING_AREA_MISMATCH_CANCEL);
        return false;
      }
    }
  }

  return true;
}

function getRequestedSeatingArea(
  timelineEvents: core.PurchaseTimelineEvent[],
  currentRequestedSeatingArea: string | undefined,
  idx: number
) {
  for (let i = idx; i < timelineEvents.length; i++) {
    if (timelineEvents[i].requestedSeatingArea) {
      return timelineEvents[i].requestedSeatingArea;
    } else if (
      currentRequestedSeatingArea &&
      timelineEvents[i].uniqueTableSeatingAreas.includes(currentRequestedSeatingArea)
    ) {
      return currentRequestedSeatingArea;
    }
  }
  return undefined;
}

export function getEventSeatingArea(
  timelineEvents: core.PurchaseTimelineEvent[],
  eventIndex: number
) {
  const event = timelineEvents[eventIndex];
  // Rescheduled or moved to another table outside the edit modal
  if (event.uniqueTableSeatingAreas.length > 0 && event.requestedSeatingArea === '') {
    return getRequestedSeatingArea(timelineEvents, undefined, eventIndex);
    // Edited the purchase through the edit modal this includes but not exclusive to just changing the date.
  } else if (event.uniqueTableSeatingAreas.length === 0 && event.requestedSeatingArea !== '') {
    const previousSeatingArea = getRequestedSeatingArea(
      timelineEvents,
      event.requestedSeatingArea,
      eventIndex + 1
    );
    return previousSeatingArea !== event.requestedSeatingArea ||
      timelineEvents.slice(eventIndex + 1).every((event) => event.requestedSeatingArea === '')
      ? event.requestedSeatingArea
      : undefined;
  }
  // Any other case is undefined
  return undefined;
}

/**
 * Create a unique string to identify a table based on it's
 * Room and Section
 */
export function getRoomAndSectionStr(table: dashboard.ITable) {
  return `${table.room}-${table.section}`;
}

export function getSeatingAreaForMultipleTables(
  tablesByIdMap: TableIdMap,
  selectedTablesId: number[]
) {
  const selectedSeatingAreasId = _.uniq(
    selectedTablesId.map((id) => tablesByIdMap[id].seatingArea?.id ?? NO_SEATING_AREA_ID)
  );
  return selectedSeatingAreasId.length === 1 ? selectedSeatingAreasId[0] : NO_SEATING_AREA_ID;
}

export function compareSeatingAreas(a: SeatingAreaOutput, b: SeatingAreaOutput): number {
  if (a.enumValue === b.enumValue) {
    return 0;
  }

  if (a.enumValue === 'DINING_ROOM') {
    return -1;
  }

  if (b.enumValue === 'DINING_ROOM') {
    return 1;
  }

  return a.name.localeCompare(b.name);
}

/**
 * Primitive soft matching for strings
 */
export const findExactMatch = (a: string, b: string) => {
  if (a === b) {
    return true;
  }

  const charsOnlyA = a.toLowerCase().replace(/[^a-z]/g, '');
  const charsOnlyB = b.toLowerCase().replace(/[^a-z]/g, '');
  const minLen = 2;
  const haveMinLength = charsOnlyA.length > minLen && charsOnlyB.length > minLen;

  return haveMinLength && (charsOnlyA.includes(charsOnlyB) || charsOnlyB.includes(charsOnlyA));
};
